/**
 * Clerk in the browser, narrowed to what the portal uses. ADR-010.
 *
 * The script itself is Clerk's, loaded from the instance's Frontend API by a
 * tag the build puts in the page head (vite.config.ts), so it downloads beside
 * the app's own bundle. That tag carries the publishable key, and Clerk's
 * script makes `window.Clerk` from it; this file waits for that and loads it.
 *
 * The sign in screens are the portal's own, driven through Clerk's resource
 * API, rather than Clerk's prebuilt components: the approved design stays, and
 * none of the components' code or styles are downloaded.
 *
 * The interfaces below are the slice of @clerk/shared's types this code calls,
 * written out so the build carries no Clerk package at all. Every call was
 * checked against clerk-js 6.34.1, the version clerk-config.ts pins.
 */

export interface ClerkFactor {
  readonly strategy: string;
  readonly emailAddressId?: string;
}

export interface SignInLike {
  readonly status: string | null;
  readonly createdSessionId: string | null;
  readonly supportedFirstFactors: readonly ClerkFactor[] | null;
  create(params: { identifier: string }): Promise<SignInLike>;
  prepareFirstFactor(params: { strategy: "email_code"; emailAddressId: string }): Promise<SignInLike>;
  attemptFirstFactor(params: { strategy: "email_code"; code: string }): Promise<SignInLike>;
  authenticateWithRedirect(params: {
    strategy: "oauth_google";
    redirectUrl: string;
    redirectUrlComplete: string;
  }): Promise<void>;
  /**
   * A passkey the browser already holds for this site, chosen in the browser's
   * own sheet; no address is typed. Only on an instance with passkeys turned on
   * (config.ts, PASSKEY_SIGN_IN), which Clerk's free plan does not include.
   */
  authenticateWithPasskey?(params: { flow: "discoverable" }): Promise<SignInLike>;
}

export interface SignUpLike {
  readonly status: string | null;
  readonly createdSessionId: string | null;
  readonly missingFields: readonly string[];
  create(params: { emailAddress: string }): Promise<SignUpLike>;
  prepareEmailAddressVerification(params: { strategy: "email_code" }): Promise<SignUpLike>;
  attemptEmailAddressVerification(params: { code: string }): Promise<SignUpLike>;
}

export interface ClerkLike {
  load(options?: Record<string, unknown>): Promise<void>;
  readonly session: { readonly id: string; getToken(): Promise<string | null> } | null | undefined;
  readonly client: { readonly signIn: SignInLike; readonly signUp: SignUpLike } | undefined;
  setActive(params: { session: string | null }): Promise<void>;
  signOut(): Promise<void>;
  handleRedirectCallback(
    params: { signInFallbackRedirectUrl: string; signUpFallbackRedirectUrl: string },
    customNavigate?: (to: string) => Promise<unknown>,
  ): Promise<unknown>;
  addListener(callback: (resources: { session?: unknown }) => void): () => void;
}

declare global {
  interface Window {
    Clerk?: ClerkLike;
  }
}

/** Clerk's script did not arrive, or would not load. */
export class ClerkUnavailable extends Error {
  override readonly name = "ClerkUnavailable";
}

/** How long to wait for the script on a slow connection before saying so. */
const SCRIPT_TIMEOUT_MS = 20_000;

let loading: Promise<ClerkLike> | null = null;

function scriptArrived(): Promise<void> {
  if (window.Clerk) return Promise.resolve();
  const tag = document.getElementById("clerk-js");
  if (!tag) {
    return Promise.reject(
      new ClerkUnavailable("This build has no Clerk publishable key. Set VITE_CLERK_PUBLISHABLE_KEY and rebuild."),
    );
  }
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(
      () => reject(new ClerkUnavailable("The sign in service took too long to load.")),
      SCRIPT_TIMEOUT_MS,
    );
    tag.addEventListener("load", () => {
      window.clearTimeout(timer);
      resolve();
    });
    tag.addEventListener("error", () => {
      window.clearTimeout(timer);
      reject(new ClerkUnavailable("The sign in service could not be reached."));
    });
  });
}

/**
 * The loaded Clerk instance. Once per page; a failure is not remembered, so
 * "Try again" really tries again.
 */
export function loadClerk(): Promise<ClerkLike> {
  if (!loading) {
    loading = (async () => {
      await scriptArrived();
      const clerk = window.Clerk;
      if (!clerk) throw new ClerkUnavailable("The sign in service loaded without starting.");
      try {
        await clerk.load();
      } catch (error) {
        throw new ClerkUnavailable(error instanceof Error ? error.message : "The sign in service would not start.");
      }
      return clerk;
    })();
    loading.catch(() => {
      loading = null;
    });
  }
  return loading;
}

/* ------------------------------------------------------------------ */
/* Clerk's errors, in the portal's words                               */
/* ------------------------------------------------------------------ */

interface ClerkApiError {
  readonly code?: string;
  readonly message?: string;
  readonly longMessage?: string;
}

/** The first error code in a Clerk API error, or null for anything else. */
export function clerkErrorCode(error: unknown): string | null {
  const errors = (error as { errors?: unknown } | null)?.errors;
  if (!Array.isArray(errors)) return null;
  const first = errors[0] as ClerkApiError | undefined;
  return typeof first?.code === "string" ? first.code : null;
}

const WORDING: Readonly<Record<string, string>> = {
  form_code_incorrect: "That code is not right. Check the email and try again.",
  verification_expired: "That code has expired. Send a new one.",
  verification_failed: "Too many wrong codes. Send a new one.",
  form_param_format_invalid: "Enter a valid email address.",
  form_identifier_invalid: "Enter a valid email address.",
  too_many_requests: "Too many attempts. Wait a minute and try again.",
  user_locked: "This account is locked for a while after too many attempts. Try again later.",
  not_allowed_access: "This email address cannot be used to sign in.",
};

/** What to tell the student about a failed Clerk call. */
export function clerkErrorMessage(error: unknown): string {
  const code = clerkErrorCode(error);
  if (code && WORDING[code]) return WORDING[code] as string;
  const errors = (error as { errors?: ClerkApiError[] } | null)?.errors;
  const first = Array.isArray(errors) ? errors[0] : undefined;
  if (first?.longMessage) return first.longMessage;
  if (first?.message) return first.message;
  if (error instanceof TypeError) return "The sign in service could not be reached. Check your connection.";
  // The portal's own refusals, thrown with words meant for the student.
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong. Try again.";
}
